<script lang="ts">
  import { api } from '$shared/api';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { addToast } from '$shared/toasts.svelte';
  import { formatRelativeTime } from '$lib/utils/time';

  interface Friend {
    id: number;
    username: string;
    avatar_url: string | null;
    last_active_at: string | null;
  }
  interface UserHit {
    id: number;
    username: string;
    avatar_url: string | null;
  }

  let friends = $state<Friend[]>([]);
  let loading = $state(true);
  let error = $state('');
  let query = $state('');

  // Add-a-friend inline search
  let addOpen = $state(false);
  let addQuery = $state('');
  let addResults = $state<UserHit[]>([]);
  let addSearching = $state(false);
  let followedIds = $state<Set<number>>(new Set());
  let addTimeout: ReturnType<typeof setTimeout> | null = null;

  $effect(() => {
    loadFriends();
  });
  $effect(() => () => {
    if (addTimeout) clearTimeout(addTimeout);
  });

  async function loadFriends() {
    loading = true;
    try {
      const res = await api.get<{ users: Friend[] }>('/users/current/friends');
      friends = res.users ?? [];
    } catch (e) {
      error = (e as Error).message;
    } finally {
      loading = false;
    }
  }

  const filtered = $derived.by(() => {
    const q = query.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((f) => f.username.toLowerCase().includes(q));
  });

  function lastPlayed(f: Friend): string {
    return f.last_active_at ? `Last played ${formatRelativeTime(f.last_active_at)}` : 'Not played yet';
  }

  // Add a friend = follow someone found via user search; they become a friend once mutual.
  function onAddInput() {
    if (addTimeout) clearTimeout(addTimeout);
    const q = addQuery.trim();
    if (q.length < 2) {
      addResults = [];
      return;
    }
    addTimeout = setTimeout(async () => {
      addSearching = true;
      try {
        const res = await api.get<{ users: UserHit[] }>(`/users/search?q=${encodeURIComponent(q)}`);
        addResults = (res.users ?? []).slice(0, 8);
      } catch {
        addResults = [];
      }
      addSearching = false;
    }, 250);
  }

  async function follow(u: UserHit) {
    if (followedIds.has(u.id)) return;
    try {
      await api.post('/follow', { followee_id: u.id });
      followedIds = new Set([...followedIds, u.id]);
      addToast(`Now following @${u.username}`, 'follow');
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Could not follow', 'error');
    }
  }
</script>

<div>
  <h1>Friends</h1>

  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if error}
    <p class="msg--error">{error}</p>
  {:else}
    <input class="input" type="search" placeholder="Search by name" bind:value={query} aria-label="Search friends" />

    {#if friends.length === 0}
      <div class="empty-state">
        <p class="empty">No friends yet.</p>
        <p class="empty-sub">Follow players — when they follow back they show up here.</p>
      </div>
    {:else}
      <h2 class="section-title">Your friends</h2>
      {#if filtered.length === 0}
        <p class="empty">No friends match “{query}”.</p>
      {:else}
        <div class="friends-list">
          {#each filtered as f (f.id)}
            <div class="friend-row">
              <a href="#/users/{f.username}" class="friend-profile">
                <Avatar username={f.username} avatarUrl={f.avatar_url} size="sm" />
                <div class="friend-info">
                  <span class="friend-name">{f.username}</span>
                  <span class="friend-sub">{lastPlayed(f)}</span>
                </div>
              </a>
            </div>
          {/each}
        </div>
      {/if}
    {/if}

    <div class="add-section">
      <p class="add-prompt">Who else do you want to play?</p>
      <button class="btn btn-primary btn-pill" onclick={() => (addOpen = !addOpen)}>Add a friend</button>

      {#if addOpen}
        <input
          class="input add-input"
          type="search"
          placeholder="Search by name"
          bind:value={addQuery}
          oninput={onAddInput}
          aria-label="Find a user to follow"
        />
        {#if addSearching}<p class="searching">Searching…</p>{/if}
        {#if addResults.length > 0}
          <div class="friends-list">
            {#each addResults as u (u.id)}
              <div class="friend-row">
                <a href="#/users/{u.username}" class="friend-profile">
                  <Avatar username={u.username} avatarUrl={u.avatar_url} size="sm" />
                  <span class="friend-name">{u.username}</span>
                </a>
                <button
                  class="btn {followedIds.has(u.id) ? 'btn-outline' : 'btn-primary'} btn-pill btn-sm"
                  onclick={() => follow(u)}
                  disabled={followedIds.has(u.id)}
                >
                  {followedIds.has(u.id) ? 'Following' : 'Follow'}
                </button>
              </div>
            {/each}
          </div>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  h1 {
    margin-bottom: 1.5rem;
  }
  .input {
    margin-bottom: 1.5rem;
  }
  .section-title {
    margin: 0 0 0.75rem;
  }
  .empty,
  .empty-sub {
    color: var(--text-secondary);
  }
  .empty-state {
    text-align: center;
    padding: 2rem 1rem;
  }
  .empty-sub {
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  .friends-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .friend-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .friend-profile {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: var(--text);
    flex: 1;
    min-width: 0;
  }
  .friend-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .friend-name {
    font-weight: 600;
  }
  .friend-sub {
    font-size: 0.75rem;
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .btn-sm {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }

  .add-section {
    margin-top: 2rem;
    text-align: center;
  }
  .add-prompt {
    color: var(--text-secondary);
    margin-bottom: 0.75rem;
  }
  .add-input {
    margin-top: 1rem;
    text-align: left;
  }
  .searching {
    font-size: 0.8125rem;
    color: var(--text-dim);
    margin: 0.5rem 0;
  }
</style>
